// The guide of the chain (R3066, ui-map-interface.md §6.1.14 "The guide of
// the chain", v2-the-deep.md §11.4 "The chain point").
//
// An own Sapper that carries a lane light, within `guide_px` of a lane
// light of the viewer's faction, shows where its LAY snaps: the free chain
// point nearest it. The walk is `lanechain.ts`, the twin of the server's,
// over the lane lights the picture holds, which are the lights the server
// reads for the snap. The selected Sapper comes first, then the others in
// fleet id order, and at most `guide_max` guides stand.
//
// The keeper walks the chain again only when a Sapper moved `guide_step_px`
// from the point of its last walk, when a Sapper came, went or emptied its
// stow, when a row or a lane changed, or when the selection moved (§10:
// no heavy work once a frame). The layer packs the guides each frame from
// the result, and the ghost lights paint on its sheet.

import { SeaObjectKind, type SeaObjectStock } from '../../gen/proto/aquaphobia';
import { chainNearest, freeChainPoints, type ChainPt, type ChainTrack } from '../../lanechain';

/** The rule figures of the chain, off the catalog: the reach of a lane
 *  light, the chain spacing and the snap. */
export interface ChainFigures { reach: number; chain: number; snap: number }

/** What the guide reads of an own Sapper. */
export interface GuideSapper { id: number; x: number; y: number; stow: readonly SeaObjectStock[] }

/** One guide: the Sapper at its walk, the light and the chain point the
 *  LAY snaps to, and the free chain points of that light (the ghosts). */
export interface LaneGuide {
  sapper: number;
  sx: number; sy: number;
  lx: number; ly: number;
  cx: number; cy: number;
  ghosts: ChainPt[];
}

/** Does the stow hold a lane light. */
export function carriesLaneLight(stow: readonly SeaObjectStock[]): boolean {
  return stow.some((s) => s.kind === SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT && s.count > 0);
}

/** Does a Sapper at (x, y) stand inside the snap of its guide: the LAY
 *  then lays on the chain point. */
export function insideSnap(g: LaneGuide, x: number, y: number, snap: number): boolean {
  const dx = x - g.cx, dy = y - g.cy;
  return dx * dx + dy * dy <= snap * snap;
}

/**
 * The guides of the own Sappers: each one that carries a lane light and
 * whose nearest free chain point belongs to a light within `guidePx` of
 * it. `lights` are the lane lights of the faction in id order.
 */
export function laneGuides(sappers: readonly GuideSapper[], lights: readonly ChainPt[], tracks: readonly ChainTrack[],
  fig: ChainFigures, selected: number, guidePx: number, max: number): LaneGuide[] {
  if (lights.length === 0 || tracks.length === 0 || !(fig.chain > 0) || max <= 0) return [];
  const order = [...sappers].sort((a, b) => (a.id === selected ? -1 : b.id === selected ? 1 : a.id - b.id));
  const out: LaneGuide[] = [];
  for (const s of order) {
    if (out.length >= max) break;
    if (!carriesLaneLight(s.stow)) continue;
    const hit = chainNearest(tracks, lights, s.x, s.y, fig.reach, fig.chain);
    if (!hit) continue;
    const l = lights[hit.light];
    if ((l.x - s.x) ** 2 + (l.y - s.y) ** 2 > guidePx * guidePx) continue;
    out.push({
      sapper: s.id, sx: s.x, sy: s.y, lx: l.x, ly: l.y, cx: hit.x, cy: hit.y,
      ghosts: freeChainPoints(tracks, lights, hit.light, fig.reach, fig.chain),
    });
  }
  return out;
}

/** What the keeper reads each frame. */
export interface GuideInput {
  sappers: readonly GuideSapper[];
  /** The lane lights of the faction in id order, read at a walk alone. */
  lights: () => readonly ChainPt[];
  /** A counter that moves on every change of a sea object row. */
  rev: number;
  tracks: readonly ChainTrack[];
  fig: ChainFigures | null;
  selected: number;
  guidePx: number;
  max: number;
  step: number;
}

/**
 * Keeps the guides between two walks. `get` walks again on an edge (a
 * Sapper moved a step, a Sapper or its lane light came or went, a row, a
 * lane, the figures or the selection changed) and answers the guides; `rev`
 * moves when the guides moved, so the sheet of the ghosts paints again then
 * alone.
 */
export class LaneGuideKeeper {
  guides: LaneGuide[] = [];
  /** Moves each time the guides change. */
  rev = 0;
  /** How many walks ran. The tests read it. */
  walks = 0;
  private at = new Map<number, { x: number; y: number; carry: boolean }>();
  private rowRev = NaN;
  private tracks: readonly ChainTrack[] | null = null;
  private fig: ChainFigures | null = null;
  private selected = NaN;

  get(i: GuideInput): readonly LaneGuide[] {
    if (!this.edge(i)) return this.guides;
    this.walks++;
    this.at.clear();
    for (const s of i.sappers) this.at.set(s.id, { x: s.x, y: s.y, carry: carriesLaneLight(s.stow) });
    this.rowRev = i.rev; this.tracks = i.tracks; this.fig = i.fig; this.selected = i.selected;
    const next = i.fig ? laneGuides(i.sappers, i.lights(), i.tracks, i.fig, i.selected, i.guidePx, i.max) : [];
    if (guideKey(next) !== guideKey(this.guides)) this.rev++;
    this.guides = next;
    return next;
  }

  private edge(i: GuideInput): boolean {
    if (i.rev !== this.rowRev || i.tracks !== this.tracks || i.selected !== this.selected) return true;
    const f = this.fig, g = i.fig;
    if (f !== g && (!f || !g || f.reach !== g.reach || f.chain !== g.chain || f.snap !== g.snap)) return true;
    if (i.sappers.length !== this.at.size) return true;
    const step2 = i.step * i.step;
    for (const s of i.sappers) {
      const a = this.at.get(s.id);
      if (!a || a.carry !== carriesLaneLight(s.stow)) return true;
      if ((s.x - a.x) ** 2 + (s.y - a.y) ** 2 >= step2) return true;
    }
    return false;
  }
}

/** A key of the geometry of the ghosts. */
function guideKey(gs: readonly LaneGuide[]): string {
  return gs.map((g) => `${g.cx},${g.cy}:${g.ghosts.map((p) => `${p.x},${p.y}`).join(';')}`).join('|');
}
